import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { computeMaintStatus, STATUS_COLORS, fmtNum, fmtDate } from "../lib/status";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Wrench, ClipboardList, Archive, ArchiveRestore, Edit2, Save, X, Loader2, AlertCircle, DollarSign, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function UnitDetail() {
  const { unitId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [unit, setUnit] = useState(null);
  const [logs, setLogs] = useState([]);
  const [audit, setAudit] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    try {
      const [u, l, a, i] = await Promise.all([
        api.get(`/fleet/${unitId}`),
        api.get(`/fleet/${unitId}/service-logs`),
        api.get(`/audit?entity_id=${unitId}&limit=100`),
        api.get(`/intervals`),
      ]);
      setUnit(u.data);
      setLogs(l.data);
      setAudit(a.data);
      setIntervals(i.data);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  };
  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); /* eslint-disable-next-line */ }, [unitId]);

  if (notFound) return (
    <div className="text-center py-16">
      <div className="text-lg font-semibold text-zinc-300 mb-1">Unit not found</div>
      <Link to="/fleet" className="text-sm text-red-400 hover:text-red-300">← Back to Fleet</Link>
    </div>
  );

  if (!unit) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;

  const status = computeMaintStatus(unit, intervals);
  const colors = STATUS_COLORS[status.key];

  const costTotals = logs.reduce((acc, l) => {
    const p = Number(l.parts_cost) || 0;
    const lb = Number(l.labor_cost) || 0;
    acc.parts += p;
    acc.labor += lb;
    acc.total += (l.total_cost != null ? Number(l.total_cost) : p + lb);
    if (p || lb || l.total_cost != null) acc.count += 1;
    return acc;
  }, { parts: 0, labor: 0, total: 0, count: 0 });
  const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const startEdit = () => {
    setForm({
      unit: unit.unit || "", type: unit.type || "", division: unit.division || "",
      driver: unit.driver || "", desc: unit.desc || "", vin: unit.vin || "",
      vinAdmin: unit.vinAdmin || "", fuelCard: unit.fuelCard || "",
      status: unit.status || "", dotDate: unit.dotDate || "", tagExp: unit.tagExp || "",
      notes: unit.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { payload[k] = v === "" ? null : v; });
      await api.patch(`/fleet/${unitId}`, payload);
      toast.success("Saved");
      setEditing(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const updateNum = async (field, value) => {
    if (value === "" || value == null) return;
    try {
      await api.patch(`/fleet/${unitId}`, { [field]: Number(value) });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleArchive = async () => {
    try {
      if (unit.archived) await api.post(`/fleet/${unitId}/unarchive`);
      else await api.post(`/fleet/${unitId}/archive`);
      toast.success(unit.archived ? "Restored" : "Archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="unit-detail-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => nav("/fleet")} data-testid="back-to-fleet"
          className="p-2 border border-zinc-800 hover:bg-zinc-900 rounded-sm transition-colors shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-mono">{unit.unit}</h1>
            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${colors.badge}`}>{status.label}</span>
            {unit.archived && <span className="text-[10px] font-mono uppercase px-2 py-0.5 border border-zinc-700 text-zinc-500">ARCHIVED</span>}
          </div>
          <p className="text-sm text-zinc-300 mt-1">{unit.desc || "—"}</p>
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
            {unit.type}{unit.division ? ` · ${unit.division}` : ""}{unit.driver ? ` · ${unit.driver}` : ""}
          </p>
        </div>
        {isAdmin && !editing && (
          <button onClick={startEdit} data-testid="unit-edit-btn"
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors flex items-center gap-1.5 shrink-0">
            <Edit2 className="h-3 w-3" /> Edit
          </button>
        )}
      </div>

      {unit.dataCheck && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm px-3 py-2 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
          <span className="text-sm text-amber-300 font-mono">{unit.dataCheck}</span>
        </div>
      )}

      {/* Quick update */}
      <div className="grid grid-cols-2 gap-3">
        <QuickUpdate label="Mileage" value={unit.mileage} onSave={(v) => updateNum("mileage", v)} testId="quick-mileage" canEdit={isAdmin} />
        <QuickUpdate label="Hours" value={unit.hours} onSave={(v) => updateNum("hours", v)} testId="quick-hours" canEdit={isAdmin} />
      </div>

      {/* Editing or Specs */}
      {editing ? (
        <EditPanel form={form} setForm={setForm} onSave={saveEdit} onCancel={() => setEditing(false)} busy={busy} isAdmin={isAdmin} />
      ) : (
        <SpecsPanel unit={unit} isAdmin={isAdmin} />
      )}

      {/* Service Timeline */}
      <MonthlyCostChart logs={logs} />

      <div className="border border-zinc-800 rounded-sm" data-testid="service-history">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2 flex-wrap">
          <Wrench className="h-4 w-4 text-red-400" />
          <h3 className="text-sm font-semibold">Service History</h3>
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{logs.length} logs</span>
          {costTotals.count > 0 && (
            <div className="ml-auto flex items-center gap-3 flex-wrap" data-testid="cost-rollup">
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <DollarSign className="h-3 w-3 text-red-400" /> Total Spend
              </div>
              <span className="text-zinc-500 text-[10px] font-mono">Parts <span className="text-zinc-200">{money(costTotals.parts)}</span></span>
              <span className="text-zinc-500 text-[10px] font-mono">Labor <span className="text-zinc-200">{money(costTotals.labor)}</span></span>
              <span data-testid="cost-total" className="text-red-300 text-xs font-mono font-bold">{money(costTotals.total)}</span>
            </div>
          )}
        </div>
        <ul className="divide-y divide-zinc-800">
          {logs.length === 0 && <li className="p-4 text-sm text-zinc-500">No service logged yet. Go to Fleet, expand this unit, and use "Log Service / Inspection".</li>}
          {logs.map((l) => (
            <li key={l.id} className="p-4" data-testid={`log-${l.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${l.kind === "annual_inspection" ? "bg-red-400" : "bg-emerald-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-zinc-100">{l.kind === "annual_inspection" ? "Annual Inspection" : "Service"}</span>
                    <span className="text-xs text-zinc-500">·</span>
                    <span className="text-xs text-zinc-400 font-mono">{fmtDate(l.date)}</span>
                    <span className="text-xs text-zinc-500">·</span>
                    <span className="text-xs text-zinc-400">{l.username}</span>
                  </div>
                  <div className="mt-1 text-xs font-mono text-zinc-500 flex gap-3 flex-wrap">
                    {l.mileage != null && <span>{fmtNum(l.mileage)} mi</span>}
                    {l.hours != null && <span>{fmtNum(l.hours)} hr</span>}
                    {l.next_annual && <span>Next: {fmtDate(l.next_annual)}</span>}
                  </div>
                  {(l.parts_cost != null || l.labor_cost != null) && (
                    <div className="mt-1 text-xs font-mono flex gap-3 flex-wrap" data-testid={`log-cost-${l.id}`}>
                      {l.parts_cost != null && <span className="text-zinc-500">Parts <span className="text-zinc-200">{money(l.parts_cost)}</span></span>}
                      {l.labor_cost != null && <span className="text-zinc-500">Labor <span className="text-zinc-200">{money(l.labor_cost)}</span></span>}
                      <span className="text-red-300">Total {money(l.total_cost ?? ((Number(l.parts_cost) || 0) + (Number(l.labor_cost) || 0)))}</span>
                    </div>
                  )}
                  {l.notes && <p className="mt-1 text-sm text-zinc-300 italic">{l.notes}</p>}
                </div>
                {isAdmin && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete this ${l.kind === "annual_inspection" ? "annual inspection" : "service"} log from ${fmtDate(l.date)}?`)) return;
                      try {
                        await api.delete(`/service-logs/${l.id}`);
                        toast.success("Service log deleted");
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    data-testid={`log-delete-${l.id}`}
                    className="shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-zinc-700 hover:border-red-500/60 hover:text-red-300 rounded-sm transition-colors"
                    title="Delete this service log">
                    Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Audit log */}
      <div className="border border-zinc-800 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-zinc-400" />
          <h3 className="text-sm font-semibold">Change History</h3>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-zinc-500">{audit.length} entries</span>
        </div>
        <ul className="divide-y divide-zinc-800 max-h-[400px] overflow-y-auto">
          {audit.length === 0 && <li className="p-4 text-sm text-zinc-500">No changes recorded.</li>}
          {audit.map((a) => (
            <li key={a.id} className="p-3 text-xs" data-testid={`audit-${a.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-zinc-100 font-medium">{a.username}</span>
                <span className="font-mono uppercase text-[10px] tracking-wider text-zinc-400 border border-zinc-800 px-1.5 py-0.5">{a.action}</span>
                <span className="text-[10px] font-mono text-zinc-500 ml-auto">{fmtDate(a.timestamp)}</span>
              </div>
              {a.field && (
                <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                  {a.field}: <span className="text-red-400 line-through">{String(a.old_value ?? "—")}</span> → <span className="text-emerald-400">{String(a.new_value ?? "—")}</span>
                </div>
              )}
              {a.summary && !a.field && <p className="text-zinc-400 mt-0.5">{a.summary}</p>}
            </li>
          ))}
        </ul>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={toggleArchive} data-testid="archive-toggle"
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors flex items-center gap-1.5">
            {unit.archived ? <><ArchiveRestore className="h-3 w-3" /> Restore Unit</> : <><Archive className="h-3 w-3" /> Archive Unit</>}
          </button>
        </div>
      )}
    </div>
  );
}

function QuickUpdate({ label, value, onSave, testId, canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {!editing ? (
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-2xl sm:text-3xl text-zinc-100">{fmtNum(value)}</span>
          {canEdit && (
            <button onClick={() => setEditing(true)} data-testid={`${testId}-edit`}
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors shrink-0">
              Update
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input type="number" value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid={`${testId}-input`}
            className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1 font-mono text-lg outline-none" />
          <button onClick={() => { onSave(v); setEditing(false); }} data-testid={`${testId}-save`}
            className="p-2 bg-red-600 hover:bg-red-500 rounded-sm transition-colors"><Save className="h-3 w-3" /></button>
          <button onClick={() => { setV(value ?? ""); setEditing(false); }}
            className="p-2 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors"><X className="h-3 w-3" /></button>
        </div>
      )}
    </div>
  );
}

function SpecsPanel({ unit, isAdmin }) {
  const rows = [
    ["VIN", unit.vin],
    ["Fuel Card", unit.fuelCard],
    ["DOT Date", fmtDate(unit.dotDate)],
    ["Tag Exp", unit.tagExp],
    ["Status", unit.status],
    ["Division", unit.division],
    ["Driver", unit.driver],
    ["Last Service Date", fmtDate(unit.lastServiceDate)],
    ["Last Service Mi", fmtNum(unit.lastServiceMileage)],
    ["Last Service Hr", fmtNum(unit.lastServiceHours)],
    ["Last Annual", fmtDate(unit.lastAnnualInspection)],
    ["Next Annual", fmtDate(unit.nextAnnualInspection)],
  ];
  if (isAdmin && unit.vinAdmin) rows.splice(1, 0, ["VIN Admin", unit.vinAdmin]);

  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900">
      <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
        <h3 className="text-sm font-semibold">Specs</h3>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 p-4">
        {rows.map(([label, value]) => (
          <div key={label} className="border-b border-zinc-800/60 pb-2">
            <dt className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</dt>
            <dd className="text-sm font-mono text-zinc-200 mt-1 break-all">{value || "—"}</dd>
          </div>
        ))}
      </dl>
      {unit.notes && (
        <div className="border-t border-zinc-800 p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
          <p className="text-sm text-zinc-300 italic">{unit.notes}</p>
        </div>
      )}
    </div>
  );
}

function EditPanel({ form, setForm, onSave, onCancel, busy, isAdmin }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="border border-red-500/40 rounded-sm bg-zinc-900">
      <div className="px-4 py-3 border-b border-zinc-800 bg-red-500/5 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-red-200">Edit Specs</h3>
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-xs font-mono uppercase px-3 py-1.5 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors">Cancel</button>
          <button onClick={onSave} disabled={busy} data-testid="edit-save"
            className="text-xs font-mono uppercase px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded-sm transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Fld label="Unit" k="unit" form={form} set={set} disabled={!isAdmin} />
        <Fld label="Type" k="type" form={form} set={set} disabled={!isAdmin} />
        <Fld label="Description" k="desc" form={form} set={set} />
        <Fld label="Division" k="division" form={form} set={set} />
        <Fld label="Driver" k="driver" form={form} set={set} />
        <Fld label="Fuel Card" k="fuelCard" form={form} set={set} />
        <Fld label="VIN" k="vin" form={form} set={set} disabled={!isAdmin} />
        {isAdmin && <Fld label="VIN Admin" k="vinAdmin" form={form} set={set} />}
        <Fld label="DOT Date (YYYY-MM-DD)" k="dotDate" form={form} set={set} />
        <Fld label="Tag Exp" k="tagExp" form={form} set={set} />
        <Fld label="Status" k="status" form={form} set={set} />
        <div className="sm:col-span-2">
          <Fld label="Notes" k="notes" form={form} set={set} />
        </div>
      </div>
    </div>
  );
}

function Fld({ label, k, form, set, disabled }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} disabled={disabled} data-testid={`edit-${k}`}
        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded-sm px-2 py-1.5 font-mono text-sm outline-none disabled:opacity-60" />
    </div>
  );
}

function MonthlyCostChart({ logs }) {
  // Aggregate last 12 months of parts + labor
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    months.push({
      key,
      label: d.toLocaleString(undefined, { month: "short" }) + (d.getMonth() === 0 || i === 11 ? ` '${String(d.getFullYear()).slice(2)}` : ""),
      parts: 0,
      labor: 0,
      total: 0,
    });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  logs.forEach((l) => {
    if (!l.date) return;
    const key = l.date.slice(0, 7);
    const i = idx[key];
    if (i == null) return;
    const p = Number(l.parts_cost) || 0;
    const lb = Number(l.labor_cost) || 0;
    months[i].parts += p;
    months[i].labor += lb;
    months[i].total += l.total_cost != null ? Number(l.total_cost) : p + lb;
  });
  const hasAny = months.some((m) => m.total > 0);

  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900" data-testid="monthly-cost-chart">
      <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-red-400" />
        <h3 className="text-sm font-semibold">Monthly Cost · Last 12 Months</h3>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-zinc-500">Parts + Labor</span>
      </div>
      {!hasAny ? (
        <div className="p-6 text-sm text-zinc-500">No cost data yet. Log a service with parts or labor costs to see this chart.</div>
      ) : (
        <div className="p-3" style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={months} margin={{ top: 10, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#71717a", fontFamily: "monospace", fontSize: 10 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontFamily: "monospace", fontSize: 10 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`)} width={48} />
              <Tooltip
                cursor={{ fill: "rgba(239, 68, 68, 0.06)" }}
                contentStyle={{ background: "#09090b", border: "1px solid #3f3f46", borderRadius: 2, fontFamily: "monospace", fontSize: 11 }}
                labelStyle={{ color: "#e4e4e7" }}
                formatter={(value, name) => [`$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, name === "parts" ? "Parts" : "Labor"]}
              />
              <Bar dataKey="parts" stackId="cost" fill="#71717a" radius={[0, 0, 0, 0]} />
              <Bar dataKey="labor" stackId="cost" fill="#ef4444" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
